#!/usr/bin/env node
// drawing-check.mjs — mark a candidate's SKETCH AND LABEL answer by COMPUTE, never by eye.
//
// WHY THIS EXISTS. The 2M paper's Part A carries a compulsory ship-construction question that is
// answered with a drawing. It is the one part of the syllabus a text tutor cannot touch, so a
// candidate can drill everything else and still meet that question cold on the day.
//
// WHY NOT A VISION MODEL. Asking a vision model "is this a good midship section?" returns a plausible
// sentence with nothing behind it — the same free generation of fact that FP-018 forbids everywhere
// else in this tutor. A mark is a fact about the candidate. So the reference is not a picture: it is
// GEOMETRY (named members with coordinates), the candidate's answer is geometry (labelled strokes),
// and every mark is a computation over the two. Where a computation is not available the answer is a
// typed NULL — "not assessed", with the reason — never a guess dressed as a mark.
//
// WHAT CARRIES MARKS, AND WHY THAT SPLIT.
//   · PRESENCE + LABEL — computed by matching the candidate's label text to the member's name and
//     aliases. Reliable, and it is what "sketch AND label" actually examines.
//   · RELATIONS — computed on the candidate's OWN coordinates (is the tank top above the bottom
//     shell? does the hopper plate land on both the tank top and the side shell?). Scale-free and
//     position-free, so no fitting is involved and the result cannot be an artefact of alignment.
//   · PLACEMENT and ORIENTATION are DIAGNOSTIC ONLY. They need the candidate's sketch fitted to the
//     model (translation + uniform scale, no rotation — a sketch may be any size, anywhere on the
//     page, but not sideways), and a fit can fail. Feedback from a fit is useful; a MARK from a fit
//     would be a mark that depends on the fitting working. It does not carry marks.
//
//   node drawing-check.mjs list · spec <id> · demo <id> · check <id> <submission.json>
import { readFileSync, readdirSync, existsSync } from 'node:fs';
import { shipFor } from './fleet.mjs';
import { canonicalise } from './answer-input.mjs';

const DIR = new URL('./drawings/', import.meta.url).pathname;

// ── specs ───────────────────────────────────────────────────────────────────
/* SPECS MAY BE INJECTED, SO THIS ENGINE CAN RUN IN A BROWSER (2026-08-12).
 *
 * Founder: "if slm can be included in digital university, then why not full marking engine." He is
 * right, and my earlier size objection was weak — measured, the engine and all 202 specs are 0.54 MB
 * gzipped against ~4.6 GB for a Qwen2.5-7B. That is 0.012%. Arguing 0.54 MB was too heavy while
 * planning to ship 4.6 GB was the wrong reasoning.
 *
 * The REAL blocker was never size, it was `node:fs`: loadSpecs scans a directory. So the specs can
 * now be handed in instead, and the marking arithmetic below is untouched — one engine, two ways of
 * finding its data, exactly as almanac-body.mjs is one builder with two callers. A second browser
 * implementation of marking would drift from this one silently.
 *
 * NOTE THE LAYERING the founder set out: the SLM is OPTIONAL, so nothing here may depend on it.
 * This engine is deterministic arithmetic over authored specs and calls no model at all — which is
 * why it can sit in the tier BELOW the SLM and still work when the SLM is absent (FP-010). */
let _injected = null;
export function useSpecs(specs) { _injected = Array.isArray(specs) && specs.length ? specs : null; }

export function loadSpecs() {
  if (_injected) return _injected;
  if (typeof readdirSync !== 'function' || !existsSync || !existsSync(DIR)) return [];
  // a leading underscore marks a data file that is NOT a drawing (the corpus uses the same
  // convention for _2m-ladder.json). Without it, _member-detail.json loads as a spec with no parts.
  return readdirSync(DIR).filter((f) => f.endsWith('.json') && !f.startsWith('_'))
    .map((f) => { try { return JSON.parse(readFileSync(DIR + f, 'utf8')); } catch { return null; } })
    .filter(Boolean);
}
export function getSpec(id) { return loadSpecs().find((s) => s.id === id) || null; }

/**
 * The mark scheme is a COMPILED VIEW of the parts and relations, so a declared paper total that does
 * not equal the sum of its allocations is a defect in the drawing, not a rounding matter. Also
 * catches a part whose geometry cannot be resolved and an alias colliding across two members —
 * either would silently mis-mark a candidate.
 */
export function validate() {
  const out = [];
  for (const raw of loadSpecs()) {
    const s = resolved(raw);
    const sum = s.parts.reduce((t, p) => t + (p.marks || 0), 0)
              + (s.relations || []).reduce((t, r) => t + (r.marks || 0), 0);
    const faults = [];
    if (sum !== s.marks) faults.push(`declared ${s.marks} marks, allocations sum to ${sum}`);
    if (s.geometry_missing.length) faults.push(`no geometry for: ${s.geometry_missing.join(', ')}`);
    const seen = new Map();
    for (const p of s.parts) for (const n of [p.label, ...(p.aliases || [])]) {
      const k = normLabel(n);
      if (seen.has(k) && seen.get(k) !== p.ref) faults.push(`alias "${n}" is claimed by both ${seen.get(k)} and ${p.ref}`);
      seen.set(k, p.ref);
    }
    for (const r of s.relations || []) for (const n of [r.a, ...(Array.isArray(r.b) ? r.b : r.b ? [r.b] : [])])
      if (!s.parts.some((p) => p.ref === n)) faults.push(`relation refers to unknown member "${n}"`);
    out.push({ id: s.id, marks: s.marks, allocated: sum, parts: s.parts.length, relations: (s.relations || []).length, faults });
  }
  return { kind: 'drawing_validation', drawings: out, faults: out.reduce((t, d) => t + d.faults.length, 0) };
}

/**
 * Resolve a spec's parts to concrete geometry. A drawing either declares its own coordinates or
 * names a VESSEL, in which case the member coordinates come from the ship herself — the shell line
 * of the midship section is read from the table of offsets, not drawn by hand.
 */
export function resolved(spec) {
  if (!spec) return null;
  /* ALREADY RESOLVED — pass it through. A spec arriving with its geometry already merged (every part
     carrying `geom`, and an `extent`) has nothing left to look up, and re-running the lookup would
     need the fleet. That is what lets the BROWSER hold this engine without also holding eight
     complete hull definitions: the server resolves once and sends the result, so `node:fs` and
     fleet.mjs both stay ashore while the marking arithmetic below is byte-identical.
     Measured 2026-08-12: resolved specs are 179 KB gzipped, against 135 KB for raw specs PLUS 48 KB
     of fleet in the bundle — slightly smaller in total, and the cost moves out of the base bundle
     into the download a candidate chooses. */
  if (Array.isArray(spec.parts) && spec.extent && spec.parts.every((p) => 'geom' in p)) {
    return { ...spec, geometry_missing: spec.geometry_missing || spec.parts.filter((p) => !p.geom).map((p) => p.ref) };
  }
  // The drawing names a SHIP, and the fleet resolves her. A view is a PLANE THROUGH THAT SHIP, not a
  // separate drawing — every view reads the same offsets and the same scantlings, so a second vessel
  // needs no engine change, only her own three data blocks.
  const ship = spec.geometry_source?.vessel ? shipFor(spec.geometry_source.vessel) : null;
  const view = ship ? ship.STRUCTURE.views[spec.geometry_source?.view || 'midship'] : null;
  const src = view?.members || null;
  const parts = spec.parts.map((p) => {
    const g = src?.[p.ref] || (typeof p.geom === 'object' ? p.geom : null);
    return { ...p, geom: g ? { kind: g.kind, pts: g.pts, ...(g.r ? { r: g.r } : {}) } : null };
  });
  const missing = parts.filter((p) => !p.geom).map((p) => p.ref);
  return { ...spec, parts, extent: view ? view.extent : extentOf(parts), geometry_missing: missing };
}

function extentOf(parts) {
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const p of parts) for (const [u, v] of p.geom?.pts || []) {
    u0 = Math.min(u0, u); u1 = Math.max(u1, u); v0 = Math.min(v0, v); v1 = Math.max(v1, v);
  }
  return Number.isFinite(u0) ? { y: [u0, u1], z: [v0, v1] } : { y: [0, 1], z: [0, 1] };
}

// ── geometry ────────────────────────────────────────────────────────────────
const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1];
const norm = (a) => Math.hypot(a[0], a[1]);

/** Length-weighted centroid, principal direction, bbox and endpoints of a polyline. */
export function metrics(pts, closed = false) {
  const P = (pts || []).filter((p) => Array.isArray(p) && p.length >= 2).map((p) => [+p[0], +p[1]]);
  if (!P.length) return null;
  if (P.length === 1) return { c: P[0], len: 0, dir: null, ends: [P[0], P[0]], pts: P, closed };
  const seq = closed ? [...P, P[0]] : P;
  let len = 0, cu = 0, cv = 0;
  for (let i = 0; i + 1 < seq.length; i++) {
    const l = norm(sub(seq[i + 1], seq[i]));
    len += l; cu += l * (seq[i][0] + seq[i + 1][0]) / 2; cv += l * (seq[i][1] + seq[i + 1][1]) / 2;
  }
  const c = len > 1e-9 ? [cu / len, cv / len]
    : [P.reduce((s, p) => s + p[0], 0) / P.length, P.reduce((s, p) => s + p[1], 0) / P.length];
  // direction from the two extreme points along the chord — stable for a hand-drawn wobbly line
  const d = sub(P[P.length - 1], P[0]);
  const dir = norm(d) > 1e-9 ? ((Math.atan2(d[1], d[0]) * 180 / Math.PI) + 180) % 180 : null;
  return { c, len, dir, ends: [P[0], P[P.length - 1]], pts: P, closed };
}

/** Shortest distance from a point to a polyline. */
function distToPoly(p, pts) {
  if (!pts?.length) return Infinity;
  if (pts.length === 1) return norm(sub(p, pts[0]));
  let best = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], ab = sub(b, a), l2 = dot(ab, ab);
    const t = l2 < 1e-12 ? 0 : Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
    best = Math.min(best, norm(sub(p, [a[0] + t * ab[0], a[1] + t * ab[1]])));
  }
  return best;
}
/** Shortest distance between two polylines (sampled — enough for a tolerance test). */
function polyGap(A, B) {
  let best = Infinity;
  for (const p of A) best = Math.min(best, distToPoly(p, B));
  for (const p of B) best = Math.min(best, distToPoly(p, A));
  return best;
}

// ── label matching ──────────────────────────────────────────────────────────
const STOP = new Set(['the', 'a', 'an', 'of', 'and', 'is', 'at', 'in', 'on', 'to', 'for']);
const normLabel = (s) => canonicalise(String(s || '')).toLowerCase()
  .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
const toks = (s) => normLabel(s).split(' ').filter((w) => w && !STOP.has(w));

function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (!m || !n) return Math.max(m, n);
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

/**
 * Match one candidate label to a member. Ambiguity is reported, never resolved by preference:
 * "girder" alone is genuinely both the centre girder and the side girder, and choosing one for the
 * candidate would be inventing which member they meant.
 */
export function matchLabel(text, parts) {
  const s = normLabel(text), st = toks(text);
  if (!s) return { ok: false, reason: 'empty' };
  const names = (p) => [p.label, ...(p.aliases || [])];

  const exact = parts.filter((p) => names(p).some((n) => normLabel(n) === s));
  if (exact.length === 1) return { ok: true, ref: exact[0].ref, how: 'exact' };

  /* ── A SHORTHAND THAT FITS TWO MEMBERS IS A QUESTION, NOT AN ANSWER (2026-08-11) ──────────────
   *
   * Audited across all 202 sheets: a candidate writing "cargo tank end" was credited — `how: exact`,
   * full confidence — with **After cargo tank end**, on a sheet that also carries **Forward cargo
   * tank end**. Same for "pedestal shell" (inboard / outboard) and "hatch edge" (side / end). He may
   * have meant either, and the engine picked one silently.
   *
   * That is the one thing FP-018 forbids: guessing where the honest answer is a refusal. An examiner
   * would ask "which one?" — so the engine does too, and `ambiguous_labels` already carries that
   * back to the candidate with the candidates named.
   *
   * The rule is narrow on purpose. If the query EQUALS some member's own declared name it is
   * decisive and resolves above — "Hawse pipe" means the hawse pipe even on a sheet that also says
   * "Anchor (stowed in the hawse pipe)", and "Main engine" means the main engine even beside "Main
   * engine bedplate". Only a shorthand that names NO member, while fitting inside two or more, is
   * sent back as a question. */
  const fitsInside = parts.filter((p) => names(p).some((n) => {
    const nt = toks(n);
    return st.length > 0 && nt.length > st.length && st.every((w) => nt.includes(w));
  }));
  if (fitsInside.length > 1) {
    return { ok: false, reason: 'ambiguous', candidates: fitsInside.map((p) => p.label) };
  }

  const setEq = parts.filter((p) => names(p).some((n) => {
    const nt = toks(n); return nt.length === st.length && nt.every((w) => st.includes(w));
  }));
  if (setEq.length === 1) return { ok: true, ref: setEq[0].ref, how: 'exact' };

  // spelling — proportional to length, so short words are not fuzzily matched into each other.
  //
  // THIS RUNS BEFORE the superset test, and the order is the whole point. TEST-01, walking all 89
  // drawings on 2026-08-03, found that "Deck abve" was credited to the member DECK — because the
  // superset test saw the surviving token 'deck' and discarded 'abve' as an extra word the candidate
  // had added. The mark went to a different member, and DECK ABOVE was reported to the candidate as
  // "not drawn, or drawn without a label" when they had drawn it and labelled it. A typo cost a mark
  // AND produced a false statement about the paper.
  //
  // A near-exact match on the WHOLE string is a much tighter statement of intent than "one of your
  // tokens appears in my name", so it is tested first. "Tank top plating" is unaffected: it is 8
  // edits from "tank top", far outside tolerance, so it still falls through to the superset test.
  const spell = parts.map((p) => {
    let best = Infinity, via = null;
    for (const n of names(p)) { const d = editDistance(s, normLabel(n)); if (d < best) { best = d; via = n; } }
    return { p, d: best, via };
  }).filter((x) => x.d <= Math.max(1, Math.floor(s.length * 0.16)));
  if (spell.length === 1) return { ok: true, ref: spell[0].p.ref, how: 'spelling', accepted_as: spell[0].via };
  if (spell.length > 1) return { ok: false, reason: 'ambiguous', candidates: spell.map((x) => x.p.label) };

  // candidate wrote MORE than the member's name ("tank top plating") — all of the name is there
  const superset = parts.filter((p) => names(p).some((n) => {
    const nt = toks(n); return nt.length > 0 && nt.every((w) => st.includes(w));
  }));
  if (superset.length === 1) return { ok: true, ref: superset[0].ref, how: 'exact' };
  if (superset.length > 1) return { ok: false, reason: 'ambiguous', candidates: superset.map((p) => p.label) };

  // candidate wrote LESS than the name ("girder") — accept only if exactly one member fits
  const partial = parts.filter((p) => st.length > 0 && names(p).some((n) => {
    const nt = toks(n); return st.every((w) => nt.includes(w));
  }));
  if (partial.length === 1) return { ok: true, ref: partial[0].ref, how: 'partial' };
  if (partial.length > 1) return { ok: false, reason: 'ambiguous', candidates: partial.map((p) => p.label) };

  return { ok: false, reason: 'unrecognised' };
}

// ── matching and fitting, shared with the renderer ──────────────────────────
/** Normalise a submission's strokes to metrics + label matches. */
export function matchStrokes(spec, rawStrokes) {
  const strokes = (rawStrokes || []).map((s, i) => ({
    i, label: String(s.label || '').trim(), m: metrics(s.pts, !!s.closed),
  })).filter((s) => s.m);
  const used = new Map(), unlabelled = [], unrecognised = [], ambiguous = [], duplicates = [];
  for (const st of strokes) {
    if (!st.label) { unlabelled.push(st.i); continue; }
    const m = matchLabel(st.label, spec.parts);
    if (!m.ok) {
      (m.reason === 'ambiguous' ? ambiguous : unrecognised)
        .push({ stroke: st.i, label: st.label, ...(m.candidates ? { could_be: m.candidates } : {}) });
      continue;
    }
    if (used.has(m.ref)) { duplicates.push({ stroke: st.i, label: st.label, member: m.ref }); continue; }
    used.set(m.ref, { ...st, how: m.how, accepted_as: m.accepted_as || null });
  }
  return { strokes, used, unlabelled, unrecognised, ambiguous, duplicates };
}

/** Matched (student centroid, model centroid) pairs for the alignment. */
export function pairsFor(spec, used) {
  const pairs = [];
  for (const p of spec.parts) {
    const st = used.get(p.ref); if (!st || !p.geom) continue;
    const rm = metrics(p.geom.pts, p.geom.kind === 'polygon');
    if (rm) pairs.push({ ref: p.ref, s: st.m.c, r: rm.c, sm: st.m, rm });
  }
  return pairs;
}

/** Similarity fit with translation and uniform scale, no rotation. Needs 3+ pairs to be meaningful. */
export function fitPairs(pairs) {
  if (pairs.length < 3) return { ok: false, reason: 'fewer than three labelled members matched — placement cannot be aligned' };
  const ms = [0, 1].map((k) => pairs.reduce((t, p) => t + p.s[k], 0) / pairs.length);
  const mr = [0, 1].map((k) => pairs.reduce((t, p) => t + p.r[k], 0) / pairs.length);
  let num = 0, den = 0;
  for (const p of pairs) { const s = sub(p.s, ms), r = sub(p.r, mr); num += dot(s, r); den += dot(s, s); }
  if (den < 1e-9) return { ok: false, reason: 'the labelled members are all at one point' };
  const k = num / den;
  if (k <= 0) return { ok: false, reason: 'the sketch appears mirrored relative to the model — check which side of the centreline you are drawing' };
  return { ok: true, k, t: [mr[0] - k * ms[0], mr[1] - k * ms[1]] };
}

// ── the check ───────────────────────────────────────────────────────────────
/**
 * @param {string} id     drawing id
 * @param {object} sub    { strokes: [{ label, pts: [[u,v],...] }] } in drawing coordinates, v UP
 */
export function check(id, submission) {
  const spec = resolved(getSpec(id));
  if (!spec) return { kind: 'null', reason: 'unknown drawing', id };
  // 1 ── match labels to members
  const { strokes, used, unlabelled, unrecognised, ambiguous, duplicates } =
    matchStrokes(spec, submission?.strokes);
  if (!strokes.length) return { kind: 'null', reason: 'no strokes submitted', id };

  const scored = spec.parts.filter((p) => (p.marks || 0) > 0);

  // 2 ── the fit, from every matched member that has model geometry (diagnostic)
  const pairs = pairsFor(spec, used);
  const fit = fitPairs(pairs);
  const dgR = Math.hypot(spec.extent.y[1] - spec.extent.y[0], spec.extent.z[1] - spec.extent.z[0]);
  let placement = null;
  if (fit.ok) {
    const rows = pairs.map((p) => {
      const proj = [fit.k * p.s[0] + fit.t[0], fit.k * p.s[1] + fit.t[1]];
      return { ref: p.ref, residual: +(norm(sub(proj, p.r)) / dgR).toFixed(3) };
    }).sort((a, b) => b.residual - a.residual);
    const med = rows.length ? rows[Math.floor(rows.length / 2)].residual : 1;
    placement = med > 0.25
      ? { kind: 'null', reason: `the sketch could not be aligned to the model (median residual ${med}); presence, labels and relations are marked, placement is not assessed` }
      : { kind: 'compute', median_residual: med,
          well_placed: rows.filter((r) => r.residual <= 0.10).map((r) => r.ref),
          check_the_position_of: rows.filter((r) => r.residual > 0.18).map((r) => r.ref) };
  } else {
    placement = { kind: 'null', reason: fit.reason };
  }

  // 3 ── orientation (diagnostic; the fit carries no rotation, so directions compare directly)
  const orientation = [];
  for (const p of pairs) {
    if (p.sm.dir === null || p.rm.dir === null || p.rm.len < 1e-6) continue;
    let d = Math.abs(p.sm.dir - p.rm.dir); if (d > 90) d = 180 - d;
    if (d > 20) orientation.push({ ref: p.ref, drew_deg: +p.sm.dir.toFixed(0), model_deg: +p.rm.dir.toFixed(0), off_by_deg: +d.toFixed(0) });
  }

  // 4 ── MARKS: presence + label
  let awarded = 0, assessed = 0;
  const parts = scored.map((p) => {
    const st = used.get(p.ref);
    assessed += p.marks;
    if (st) { awarded += p.marks; return { ref: p.ref, label: p.label, marks: p.marks, got: p.marks, verdict: 'present and labelled', matched: st.how, ...(st.accepted_as ? { accepted_as: st.accepted_as } : {}) }; }
    const amb = ambiguous.find((a) => (a.could_be || []).includes(p.label));
    return { ref: p.ref, label: p.label, marks: p.marks, got: 0,
             verdict: amb ? 'not credited — your label was ambiguous' : 'not drawn, or drawn without a label',
             why: p.why || null, citation: p.citation || null };
  });

  // 5 ── MARKS: relations, computed on the candidate's own coordinates
  const dgS = (() => {
    let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
    for (const s of strokes) for (const [u, v] of s.m.pts) {
      u0 = Math.min(u0, u); u1 = Math.max(u1, u); v0 = Math.min(v0, v); v1 = Math.max(v1, v);
    }
    return Math.hypot(u1 - u0, v1 - v0) || 1;
  })();
  const M = (ref) => used.get(ref)?.m || null;
  const relations = (spec.relations || []).map((r) => evaluateRelation(r, M, dgS));
  for (const r of relations) { if (r.verdict !== 'null') { assessed += r.marks; awarded += r.got; } }
  const notAssessed = relations.filter((r) => r.verdict === 'null');

  return {
    kind: 'marked', drawing: spec.id, title: spec.title, paper_marks: spec.marks,
    awarded, assessed_marks: assessed,
    not_assessed_marks: notAssessed.reduce((t, r) => t + r.marks, 0),
    score_line: `${awarded} of ${assessed} marks assessed` +
      (notAssessed.length ? ` · ${notAssessed.reduce((t, r) => t + r.marks, 0)} mark(s) NOT assessed` : ''),
    parts, relations, placement, orientation,
    unlabelled_strokes: unlabelled.length,
    unrecognised_labels: unrecognised, ambiguous_labels: ambiguous, duplicate_labels: duplicates,
    not_in_mark_scheme: spec.parts.filter((p) => !(p.marks > 0)).map((p) => p.label),
    common_errors: spec.common_errors || [],
    honesty: 'Marks are awarded for members you LABELLED, and for relations computed on your own drawing. Placement and orientation are advice, not marks — they depend on fitting your sketch to the model, and a mark must not depend on that. Unlabelled strokes earn nothing: this question examines sketch AND label.'
      + (unlabelled.length ? ` You have ${unlabelled.length} unlabelled stroke(s).` : ''),
  };
}


/**
 * Evaluate ONE relation against placed geometry. Extracted so the build studio can reuse the exact
 * rules the marking engine applies — two copies of "what counts as connected" would drift, and the
 * studio would teach one thing while the paper marked another.
 *
 * @param r    the relation as declared in the spec
 * @param M    (ref) => metrics of that member as the candidate placed it, or null
 * @param dgS  diagonal of the candidate's own drawing, so every tolerance is scale-free
 */
export function evaluateRelation(r, M, dgS) {
  const a = M(r.a), bs = (Array.isArray(r.b) ? r.b : r.b ? [r.b] : []).map(M);
  const need = [r.a, ...(Array.isArray(r.b) ? r.b : r.b ? [r.b] : [])];
  const absent = need.filter((n) => !M(n));
  const base = { op: r.op, of: need, marks: r.marks || 0, why: r.why || null, phrase: r.phrase || null };
  if (absent.length) return { ...base, got: 0, verdict: 'null', reason: `not assessed — ${absent.join(', ')} not drawn or not labelled` };

  const mg = 0.02 * dgS, near = 0.08 * dgS, land = 0.14 * dgS;
  let ok = null;
  if (r.op === 'above') ok = a.c[1] - bs[0].c[1] > mg;
  else if (r.op === 'below') ok = bs[0].c[1] - a.c[1] > mg;
  else if (r.op === 'left_of') ok = bs[0].c[0] - a.c[0] > mg;
  else if (r.op === 'right_of') ok = a.c[0] - bs[0].c[0] > mg;
  else if (r.op === 'touches') ok = polyGap(a.pts, bs[0].pts) < near;
  else if (r.op === 'connects') {
    const [b, c] = bs;
    if (!c) ok = null;
    else {
      const e0 = a.ends[0], e1 = a.ends[1];
      ok = (distToPoly(e0, b.pts) < land && distToPoly(e1, c.pts) < land)
        || (distToPoly(e0, c.pts) < land && distToPoly(e1, b.pts) < land);
    }
  } else if (r.op === 'on_centreline') {
    const cl = M('centreline');
    if (!cl) return { ...base, got: 0, verdict: 'null', reason: 'not assessed — the centreline was not drawn and labelled, so there is nothing to measure from' };
    ok = Math.abs(a.c[0] - cl.c[0]) < 0.05 * dgS;
  } else return { ...base, got: 0, verdict: 'null', reason: `not assessed — unknown relation "${r.op}"` };

  if (ok === null) return { ...base, got: 0, verdict: 'null', reason: 'not assessed — the relation is malformed in the model' };
  return { ...base, got: ok ? (r.marks || 0) : 0, verdict: ok ? 'correct' : 'wrong' };
}

/* GUARDED — this module runs in a BROWSER now, where `process` is undefined. An unguarded CLI block
   does not fail at build time: rollup bundles it and the page dies on load with "process is not
   defined". That is exactly what happened with pzx.mjs on 2026-08-10, a green build that could not
   boot, and it is the second time this pattern has been caught. */
if (typeof process !== 'undefined' && process.argv && import.meta.url === `file://${process.argv[1]}`) {
  const [, , cmd, id, file] = process.argv;
  const out =
    cmd === 'list' ? { drawings: loadSpecs().map((s) => ({ id: s.id, rank: s.rank, subject: s.subject, title: s.title, marks: s.marks })) }
    : cmd === 'validate' ? validate()
    : cmd === 'spec' ? resolved(getSpec(id))
    : cmd === 'check' ? check(id, JSON.parse(readFileSync(file, 'utf8')))
    : cmd === 'demo' ? (() => {                       // the model answer, submitted as if by a candidate
        const s = resolved(getSpec(id));
        return s ? check(id, { strokes: s.parts.filter((p) => p.geom).map((p) => ({ label: p.label, pts: p.geom.pts, closed: p.geom.kind === 'polygon' })) })
                 : { error: 'unknown drawing' };
      })()
    : { usage: 'drawing-check.mjs [list | spec <id> | demo <id> | check <id> <submission.json>]' };
  console.log(JSON.stringify(out, null, 2));
}
